import React from "react";
import { Box, Button, IconButton, TextField, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ClearIcon from "@mui/icons-material/Clear";
import KeyboardArrowDown from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUp from "@mui/icons-material/KeyboardArrowUp";
import Dropdown from "react-dropdown";
import type { Option } from "react-dropdown";
import "react-dropdown/style.css";
import { NumericFormat } from "react-number-format";
import DesiredDeliveryDateField from "./DesiredDeliveryDateField";
import { AdditionalItem } from "../hooks/useAdditionalList";

const ACCENT = "#007f18";

const fieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "6px",
    backgroundColor: "#fff",
    minHeight: 48,
    "& fieldset": { borderColor: "#ccc" },
    "&:hover fieldset": { borderColor: "#b8b8b8" },
    "&.Mui-focused fieldset": { borderColor: ACCENT, borderWidth: "1px" },
  },
  "& .MuiOutlinedInput-input": {
    padding: "12px 14px",
  },
  "& .MuiInputBase-input::placeholder": {
    color: "#9a9a9a",
    opacity: 1,
  },
} as const;

const sizeDropdownArrowSx = {
  fontSize: 20,
  color: "#666",
  display: "block",
} as const;

interface AdditionalListSectionProps {
  sizeOptions: readonly string[];
  rows: AdditionalItem[];
  canDelete: boolean;
  onAddRow: () => void;
  onUpdateRow: (
    index: number,
    field: keyof AdditionalItem,
    value: string | number
  ) => void;
  onRemoveRow: (index: number) => void;
  desiredDeliveryDateValue: string;
  onDesiredDeliveryDateChange: (value: string) => void;
}

const AdditionalListSection = ({
  sizeOptions,
  rows,
  canDelete,
  onAddRow,
  onUpdateRow,
  onRemoveRow,
  desiredDeliveryDateValue,
  onDesiredDeliveryDateChange,
}: AdditionalListSectionProps) => {
  const sizeDropdownOptions = React.useMemo(
    () => sizeOptions.map((s) => ({ value: s, label: s })),
    [sizeOptions]
  );

  return (
    <Box className="additional-list-wrap">
      <Typography className="additional-list-title" component="h2">
        ADDITIONAL LIST
      </Typography>
      <Box className="additional-list-divider" component="hr" />

      <Box className="additional-list-head">
        <Typography component="span">PLANT NAME</Typography>
        <Typography component="span">SIZE</Typography>
        <Typography component="span">QTY</Typography>
      </Box>

      {rows.map((row, index) => (
        <Box
          key={`additional-row-${index}`}
          className={`additional-list-form ${canDelete ? "has-delete" : ""}`}>
          <TextField
            size="small"
            fullWidth
            placeholder="Plant name"
            value={row.plant_name}
            onChange={(event) =>
              onUpdateRow(index, "plant_name", event.target.value)
            }
            sx={fieldSx}
          />

          <Box
            className="additional-list-size-select-wrap"
            sx={{
              position: "relative",
              width: "100%",
              minWidth: 0,
              maxWidth: "100%",
            }}>
            <Dropdown
              className="additional-list-size-dropdown-root"
              options={sizeDropdownOptions}
              value={row.size ? row.size : undefined}
              onChange={(selected: Option) =>
                onUpdateRow(index, "size", selected.value)
              }
              placeholder="Size"
              placeholderClassName="additional-list-size-dropdown-placeholder"
              arrowClosed={<KeyboardArrowDown sx={sizeDropdownArrowSx} />}
              arrowOpen={<KeyboardArrowUp sx={sizeDropdownArrowSx} />}
            />
          </Box>

          <NumericFormat
            customInput={TextField}
            size="small"
            value={row.quantity}
            thousandSeparator=","
            allowNegative={false}
            decimalScale={0}
            onValueChange={(values) =>
              onUpdateRow(index, "quantity", Number(values.floatValue || 0))
            }
            sx={fieldSx}
            maxLength={5}
            inputMode="numeric"
          />

          {canDelete && (
            <Box
              sx={{
                width: "32px",
                display: "flex",
                justifyContent: "center",
              }}>
              <IconButton
                size="small"
                onClick={() => onRemoveRow(index)}
                aria-label="remove additional row">
                <ClearIcon fontSize="small" />
              </IconButton>
            </Box>
          )}
        </Box>
      ))}

      <Box className="additional-list-action">
        <Button
          variant="outlined"
          disableElevation
          onClick={onAddRow}
          className="additional-list-add-btn"
          startIcon={<AddIcon sx={{ color: ACCENT, fontSize: 22 }} />}>
          Add
        </Button>
      </Box>

      <Box className="delivery-date-wrap">
        <DesiredDeliveryDateField
          label={
            <Typography
              component="div"
              className="delivery-date-label"
              sx={{ mb: 1.25, fontWeight: 600 }}>
              Desired Delivery Date
            </Typography>
          }
          value={desiredDeliveryDateValue}
          onChange={onDesiredDeliveryDateChange}
        />
      </Box>
    </Box>
  );
};

export default AdditionalListSection;
